/**
 * Exact rational numbers. Every algebra answer is a Rational so that
 * "3/4", "0.75" and "0,75" all compare equal without floating-point error.
 *
 * Invariant (normalized form): d > 0, gcd(|n|, d) === 1, both safe integers.
 */
export interface Rational {
  readonly n: number;
  readonly d: number;
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

export function rat(n: number, d = 1): Rational {
  if (!Number.isInteger(n) || !Number.isInteger(d)) throw new Error(`rat() needs integers, got ${n}/${d}`);
  if (d === 0) throw new Error('rat() denominator is zero');
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  // `+ 0` normalizes -0 to 0
  return { n: n / g + 0, d: d / g };
}

export const int = (n: number): Rational => rat(n, 1);

export const add = (a: Rational, b: Rational): Rational => rat(a.n * b.d + b.n * a.d, a.d * b.d);
export const sub = (a: Rational, b: Rational): Rational => rat(a.n * b.d - b.n * a.d, a.d * b.d);
export const mul = (a: Rational, b: Rational): Rational => rat(a.n * b.n, a.d * b.d);
export const div = (a: Rational, b: Rational): Rational => {
  if (b.n === 0) throw new Error('division by zero');
  return rat(a.n * b.d, a.d * b.n);
};
export const neg = (a: Rational): Rational => rat(-a.n, a.d);

export const eq = (a: Rational, b: Rational): boolean => a.n === b.n && a.d === b.d;
export const isInteger = (a: Rational): boolean => a.d === 1;
export const toNumber = (a: Rational): number => a.n / a.d;
export const key = (a: Rational): string => (a.d === 1 ? `${a.n}` : `${a.n}/${a.d}`);

/** True when the value has a finite decimal expansion (denominator only has factors 2 and 5). */
export function isTerminating(a: Rational): boolean {
  let d = a.d;
  while (d % 2 === 0) d /= 2;
  while (d % 5 === 0) d /= 5;
  return d === 1;
}
